/* storybook-check-ignore */
import { useRef, useState } from 'react';

import { Box, PressableBox, Text } from '@opendoor/bricks-next';
import { Link } from '@opendoor/bricks/core';
import { legacy } from '@opendoor/bricks/theme';
import dynamic from 'next/dynamic';
import Image, { ImageLoader } from 'next/image';

// Prevents hydration errors by only rendering on client
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

type ContentfulAsset = {
  description?: string;
  file: {
    url: string;
    contentType: string;
    details: {
      image?: {
        width: number;
        height: number;
      };
    };
  };
};

export const isAsset = (x: any): x is ContentfulAsset => {
  return x.file && x.file.url && x.file.contentType;
};

export const isImageAsset = (asset: ContentfulAsset): boolean => {
  return asset.file.contentType.startsWith('image/') && asset.file.details?.image != null;
};

export const isVideoAsset = (asset: ContentfulAsset): boolean => {
  return asset.file.contentType.startsWith('video/');
};

const contentfulLoader: ImageLoader = (props) => {
  const { src, width, quality } = props;
  return `${src}?w=${width}&q=${quality || 75}`;
};

type ImageProps = {
  src: string;
  description?: string;
  width: number;
  height: number;
};

const renderImage = (props: ImageProps) => {
  return (
    <Image
      loader={contentfulLoader}
      layout="responsive"
      src={props.src}
      sizes={`(max-width: ${legacy.breakpoints[3]}) 100vw, 50vw`}
      alt={props.description || 'Article image'}
      width={props.width}
      height={props.height}
    />
  );
};

type VideoProps = {
  src: string;
  description?: string;
};

const VideoPlayer = (props: VideoProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const playerRef = useRef<any>(null);

  const handlePlayClick = () => {
    setIsPlaying(true);
    setShowPlayButton(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
    setShowPlayButton(true);
  };

  return (
    <Box
      position="relative"
      pt="56.25%" // 16:9 aspect ratio
      display="flex"
      justifyContent="center"
      overflow="hidden"
      borderRadius="$16x"
      backgroundColor="$backgroundInversePrimary"
    >
      <ReactPlayer
        ref={playerRef}
        url={props.src}
        controls
        playing={isPlaying}
        onPlay={handlePlayClick}
        onPause={handlePause}
        playsinline
        width="100%"
        height="100%"
        style={{ position: 'absolute', left: 0, top: 0 }}
      />
      {showPlayButton && (
        <PressableBox
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          cursor="pointer"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onPress={handlePlayClick}
          aria-label="Play video"
          analyticsName="video-play-button"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M21 14.5833L9.33337 22.1667L9.33337 7L21 14.5833Z" fill="white" />
          </svg>
        </PressableBox>
      )}
    </Box>
  );
};

type AssetProps = {
  src: string;
  description?: string;
  width?: number;
  height?: number;
  contentType: string;
};

const renderAsset = (props: AssetProps) => {
  // Create a ContentfulAsset-like object to use with helper functions
  const asset: ContentfulAsset = {
    file: {
      url: props.src,
      contentType: props.contentType,
      details: {
        image:
          props.width && props.height
            ? {
                width: props.width,
                height: props.height,
              }
            : undefined,
      },
    },
    description: props.description,
  };

  if (isImageAsset(asset)) {
    return renderImage({
      src: props.src,
      description: props.description,
      width: props.width as number,
      height: props.height as number,
    });
  } else if (isVideoAsset(asset)) {
    return <VideoPlayer src={props.src} description={props.description} />;
  }

  // Fallback for unsupported asset types
  return (
    <Box>
      <Text tag="p" typography="$bodyMedium">
        Unsupported asset type: {props.contentType}
      </Text>
      <Link
        href={props.src}
        target="_blank"
        aria-label={`Download ${props.description || 'asset'}`}
        analyticsName="asset-download"
      >
        Download {props.description || 'asset'}
      </Link>
    </Box>
  );
};

export default renderAsset;
